import { useMemo, useState } from 'react';
import { Input, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type LanguageOption = { readonly label: string; readonly value: string };

type LanguageMultiSelectProps = {
  value: string[];
  onChange: (next: string[]) => void;
  options: readonly LanguageOption[];
  disabled?: boolean;
};

// Radix Popover instead of DropdownMenu — this widget renders inside the
// ChatInput settings DropdownMenu, and nesting DropdownMenu in DropdownMenu
// fights for focus/dismissal. Popover is a sibling floating layer and behaves
// cleanly inside another floating layer.
const VISIBLE_CHIPS = 2;

export const LanguageMultiSelect = ({
  value,
  onChange,
  options,
  disabled,
}: LanguageMultiSelectProps) => {
  const { t } = useTranslation('chats');

  const [query, setQuery] = useState('');

  const labelByValue = useMemo(() => new Map(options.map((o) => [o.value, o.label])), [options]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  const visible = value.slice(0, VISIBLE_CHIPS);
  const overflow = value.length - visible.length;

  const toggle = (language: string) => {
    if (value.includes(language)) {
      onChange(value.filter((v) => v !== language));
    } else {
      onChange([...value, language]);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          className="relative flex items-center gap-2 w-full min-h-9 px-3 py-1.5 rounded-lg border border-border bg-card text-left text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex flex-1 flex-wrap items-center gap-1.5 pr-5">
            {visible.length === 0 ? (
              <span className="text-muted-foreground text-xs">
                {t('chats.languageMultiSelect.selectLanguages')}
              </span>
            ) : (
              visible.map((v) => (
                <span
                  key={v}
                  className="inline-flex items-center px-2 py-1 rounded-lg bg-muted text-xs text-foreground"
                >
                  {labelByValue.get(v) ?? v}
                </span>
              ))
            )}
            {overflow > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-lg bg-muted text-xs text-foreground">
                +{overflow}
              </span>
            )}
          </div>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-1 w-(--radix-popover-trigger-width) min-w-72"
      >
        <div className="p-1">
          <Input
            size="sm"
            placeholder={t('chats.languageMultiSelect.searchLanguages')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 bg-muted focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-2 py-4 text-center text-xs text-muted-foreground">
              {t('chats.languageMultiSelect.noLanguagesFound')}
            </div>
          ) : (
            filteredOptions.map((option) => {
              const selected = value.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggle(option.value)}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:bg-accent"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    {selected && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <span className="flex-1">{option.label}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
